import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image from 'quarks/Image';
import Paragraph from 'quarks/Paragraph';
import Text from 'quarks/Text';

import Breadcrumbs from 'molecules/Breadcrumbs';
import type { BreadcrumbsProps } from 'molecules/Breadcrumbs';
import Section from 'molecules/Section/Section';
import SocialShareLinks from 'molecules/SocialShareLinks';

import AuthorTooltip from 'components/ResourceComponents/AuthorTooltip';
import ComponentVideo from 'components/Video';

import type { ContentfulAsset, ContentfulComponentVideo, ContentfulEntityPerson } from 'graphqlTypes';
import type { FC } from 'react';

interface ResourceHeroProps {
  breadcrumbs: BreadcrumbsProps['breadcrumbs'];
  href: string;
  readTime: string;
  title?: string | null;
  excerpt?: string | null;
  author?: ContentfulEntityPerson | null;
  featuredReference?: ContentfulAsset | ContentfulComponentVideo | null;
  publishDate?: string;
  category?: string | null;
}

const ResourceHero: FC<ResourceHeroProps> = ({
  breadcrumbs,
  href,
  readTime,
  title,
  excerpt,
  author,
  featuredReference,
  publishDate,
  category,
}) => {
  const renderResource = () => {
    switch (featuredReference?.__typename) {
      case 'ContentfulAsset':
        return (
          <Image
            image={featuredReference}
            alt={featuredReference.title || 'Featured Image'}
            width="100%"
            loading="eager"
          />
        );
      case 'ContentfulComponentVideo':
        return <ComponentVideo {...featuredReference} isModal eagerLoadImage={true} />;
    }
  };

  return (
    <Section
      id="resource-hero"
      paddingTopMobile={48}
      paddingTopTablet={48}
      paddingTopDesktop={48}
      paddingBottomMobile={48}
      paddingBottomTablet={64}
      paddingBottomDesktop={96}
      backgroundColor="gray-100"
      disableContain={true}
      zIndex={10}
    >
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Flex
        width="100%"
        flexDirection="column"
        gap="24px"
        marginTop={48}
        md={{ gap: '32px' }}
        lg={{ flexDirection: 'row', gap: '30px' }}
      >
        <Flex flexDirection="column" flexWrap="wrap" lg={{ width: '50%', justifyContent: 'space-between' }}>
          {category && (
            <Text
              id="resource-category"
              textStyle="eyebrowSm"
              fontWeight="extraBold"
              textColor="red-700"
              marginBottom={8}
              md={{ textStyle: 'eyebrowLg', fontWeight: 'extraBold' }}
            >
              {category}
            </Text>
          )}
          {title && (
            <Heading
              as="h1"
              id="resource-title"
              textStyle="xs"
              fontWeight="extraBold"
              textColor="gray-900"
              marginBottom={16}
              md={{ textStyle: 'sm', fontWeight: 'extraBold' }}
              lg={{ textStyle: 'md', fontWeight: 'extraBold' }}
            >
              {title}
            </Heading>
          )}
          {excerpt && (
            <Paragraph
              id="resource-excerpt"
              textStyle="lg"
              textColor="gray-700"
              marginBottom={24}
              md={{ textStyle: 'xl' }}
            >
              {excerpt}
            </Paragraph>
          )}
          <Flex
            flexDirection="column"
            gap="24px"
            md={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'end' }}
          >
            {author && <AuthorTooltip {...author} readTime={readTime} publishDate={publishDate} showTooltip={true} />}
            {title && (
              <SocialShareLinks
                title={title}
                href={href}
                iconColor="gray-400"
                socialShares={['twitter', 'linkedin', 'copy-link']}
              />
            )}
          </Flex>
        </Flex>
        {featuredReference && (
          <Flex width="100%" md={{ maxHeight: '460px' }} lg={{ width: '50%' }}>
            {renderResource()}
          </Flex>
        )}
      </Flex>
    </Section>
  );
};

export default ResourceHero;
